import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { publicSupabaseConfig } from "../domain/env";
import { useAuth } from "../state/AuthProvider";
import { useLocale } from "../state/i18n";
import { ErrorFlash } from "../components/ErrorFlash";
import { PasswordInput } from "../components/PasswordInput";
import { PrototypeBanner, PublicNav } from "../components/ui";

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const hostedReset = publicSupabaseConfig() !== null;
  const { resetPassword } = useAuth();
  const { t } = useLocale();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(
    token || hostedReset ? "" : "This reset link expired. Request a new one.",
  );
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token && !hostedReset) return;
    setBusy(true);
    setError("");
    const result = await resetPassword(token, password, confirmPassword);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate("/signin?reset=1");
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <PublicNav />
        <div className="auth-panel">
          <p className="meta">{t("reset.meta")}</p>
          <h1>{t("reset.title")}</h1>
          <p className="lede">{t("reset.lede")}</p>
          <form className="stack" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="password">{t("reset.password")}</label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={setPassword}
                required
                disabled={!token && !hostedReset}
              />
            </div>
            <div className="field">
              <label htmlFor="confirm-password">{t("reset.confirm")}</label>
              <PasswordInput
                id="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                required
                disabled={!token && !hostedReset}
              />
            </div>
            {error ? <ErrorFlash key={error} message={error} /> : null}
            <div className="actions">
              <button type="submit" disabled={busy || (!token && !hostedReset)}>
                {busy ? t("reset.busy") : t("reset.submit")}
              </button>
              <Link className="btn ghost" to="/forgot-password">
                Request a new link
              </Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
